import React from "react";
import { Link, Route } from "react-router-dom";

import NotFound from "../components/NotFound";
import RequireAuth from "../components/RequireAuth";
import { AUTH_LEVELS } from "../lib/globals";
import SettingsLoader from "../loaders/SettingsLoader";
import Advertisers, {
    loader as AdvertisersLoader,
} from "../pages/SiteManagement/Advertisers";
import Advertiser, {
    loader as AdvertiserLoader,
} from "../pages/SiteManagement/Advertisers/components/Advertiser";
import DateSettings from "../pages/SiteManagement/DateSettings";
import DisplaySettings from "../pages/SiteManagement/DisplaySettings";
import SiteManagementIndex, {
    loader as IndexLoader,
} from "../pages/SiteManagement/Index";
import SiteManagementLayout from "../pages/SiteManagement/Layout";
import PageContent from "../pages/SiteManagement/PageContent";
import PaymentReminders from "../pages/SiteManagement/PaymentReminders";
import PaymentRemindersLoader from "../pages/SiteManagement/PaymentReminders/loaders/paymentRemindersLoader";
import PhotoApproval, {
    loader as PhotoApprovalLoader,
} from "../pages/SiteManagement/PhotoApproval";
import PolicyProcedures from "../pages/SiteManagement/PolicyProcedures";
import ResumeSettings from "../pages/SiteManagement/ResumeSettings";
import RulesSettings, {
    loader as RulesLoader,
} from "../pages/SiteManagement/RulesSettings";
import TermsConditions from "../pages/SiteManagement/TermsConditions";
import UmpirePaySettings from "../pages/SiteManagement/UmpirePaySettings";
import WaiverText from "../pages/SiteManagement/Waiver";
import { ISponsor } from "../types/api";

export default (
    <Route
        element={<RequireAuth allowedUserLevels={AUTH_LEVELS.sitemanagement} />}
    >
        <Route
            path="site-management"
            handle={{
                crumb: () => <Link to="/site-management">Site Management</Link>,
            }}
            element={<SiteManagementLayout />}
        >
            <Route
                index
                element={<SiteManagementIndex />}
                loader={IndexLoader}
                handle={{
                    header: () => "Site Management",
                }}
            />
            <Route path="settings">
                <Route
                    path="date"
                    element={<DateSettings />}
                    loader={SettingsLoader}
                    handle={{
                        crumb: () => {
                            return (
                                <Link to={`/site-management/settings/date`}>
                                    Date Settings
                                </Link>
                            );
                        },
                        header: () => "Date Settings",
                    }}
                />
                <Route
                    path="display"
                    element={<DisplaySettings />}
                    loader={SettingsLoader}
                    handle={{
                        crumb: () => {
                            return (
                                <Link to={`/site-management/settings/display`}>
                                    Display Settings
                                </Link>
                            );
                        },
                        header: () => "Display Settings",
                    }}
                />
            </Route>
            <Route
                path="content"
                element={<PageContent />}
                loader={SettingsLoader}
                handle={{
                    crumb: () => {
                        return (
                            <Link to={`/site-management/content`}>
                                Page Content
                            </Link>
                        );
                    },
                    header: () => "Page Content",
                }}
            />
            <Route
                path="content/resume"
                element={<ResumeSettings />}
                loader={SettingsLoader}
                handle={{
                    crumb: () => {
                        return (
                            <Link to={`/site-management/content/resume`}>
                                Resume
                            </Link>
                        );
                    },
                    header: () => "Resume",
                }}
            />
            <Route
                path="content/waiver"
                element={<WaiverText />}
                loader={SettingsLoader}
                handle={{
                    crumb: () => {
                        return (
                            <Link to={`/site-management/content/waiver`}>
                                Waiver Text
                            </Link>
                        );
                    },
                    header: () => "Waiver Text",
                }}
            />
            <Route
                path="content/policy-and-procedures"
                element={<PolicyProcedures />}
                loader={SettingsLoader}
                handle={{
                    crumb: () => {
                        return (
                            <Link
                                to={`/site-management/content/policy-and-procedures`}
                            >
                                Policy & Procedures
                            </Link>
                        );
                    },
                    header: () => "Policy & Procedures",
                }}
            />
            <Route
                path="content/terms-and-conditions"
                element={<TermsConditions />}
                loader={SettingsLoader}
                handle={{
                    crumb: () => {
                        return (
                            <Link
                                to={`/site-management/content/terms-and-conditions`}
                            >
                                Terms & Conditions
                            </Link>
                        );
                    },
                    header: () => "Terms & Conditions",
                }}
            />
            <Route
                path="umpirepay"
                element={<UmpirePaySettings />}
                loader={SettingsLoader}
                handle={{
                    crumb: () => {
                        return (
                            <Link to={`/site-management/umpirepay`}>
                                Umpire Pay
                            </Link>
                        );
                    },
                    header: () => "Umpire Pay",
                }}
            />
            <Route
                path="photoApproval"
                element={<PhotoApproval />}
                loader={PhotoApprovalLoader}
                handle={{
                    crumb: () => {
                        return (
                            <Link to={`/site-management/photoApproval`}>
                                Photo Approvals
                            </Link>
                        );
                    },
                    header: () => "Photo Approvals",
                }}
            />
            <Route path="rules" handle={{ crumb: () => "Rules" }}>
                <Route
                    path="majorybl"
                    element={<RulesSettings />}
                    loader={params =>
                        RulesLoader({
                            ...params,
                            content: "majorybl_rules_page_content",
                        })
                    }
                    handle={{
                        crumb: () => (
                            <Link to="/site-management/rules/majorybl">
                                MAJOR YBL Rules
                            </Link>
                        ),
                        header: () => "MAJORYBL Rules",
                    }}
                />
                <Route
                    path="majorhighschool"
                    element={<RulesSettings />}
                    loader={params =>
                        RulesLoader({
                            ...params,
                            content: "majorybl_highschool_rules_page_content",
                        })
                    }
                    handle={{
                        crumb: () => (
                            <Link to="/site-management/rules/majorhighschool">
                                MAJOR High School Rules
                            </Link>
                        ),
                        header: () => "MAJOR High School Rules",
                    }}
                />
            </Route>
            <Route
                path="advertisers"
                element={<Advertisers />}
                loader={AdvertisersLoader}
                handle={{
                    crumb: () => {
                        return (
                            <Link to={`/site-management/advertisers`}>
                                Advertisers
                            </Link>
                        );
                    },
                    header: () => "Advertisers",
                }}
            >
                <Route
                    path=":advertiserId"
                    element={<Advertiser />}
                    loader={AdvertiserLoader}
                    handle={{
                        crumb: ({
                            advertiserId,
                        }: {
                            advertiserId: ISponsor["id"];
                        }) => {
                            return (
                                <Link
                                    to={`/site-management/advertisers/${advertiserId}`}
                                >
                                    Edit Advertiser
                                </Link>
                            );
                        },
                        header: () => "Edit Advertiser",
                    }}
                />
                <Route
                    path="add"
                    element={<Advertiser />}
                    loader={AdvertiserLoader}
                    handle={{
                        crumb: () => {
                            return (
                                <Link to={`/site-management/advertisers/add`}>
                                    Add Advertiser
                                </Link>
                            );
                        },
                        header: () => "Add New Advertiser",
                    }}
                />
            </Route>
            <Route
                path="payment-reminders"
                element={<PaymentReminders />}
                loader={PaymentRemindersLoader}
                handle={{
                    crumb: () => (
                        <Link to="/site-management/payment-reminders">
                            Payment Reminders
                        </Link>
                    ),
                    header: () => "Payment Reminders",
                }}
            />
            <Route
                path="*"
                element={<NotFound parent="sitemanagement" />}
                handle={{ header: () => "Site Management Page Not Found" }}
            />
        </Route>
    </Route>
);
